import fetcher from "@adora/shared/utils/fetch";
import useSWR, { Arguments, mutate } from "swr";
import useSWRMutation from "swr/mutation";
import { RelativeUrl } from "@adora/shared/utils/environment";
import { ReportingIdResponse, ReportingIds } from "../types/reporting-ids";
import { snakeToCamel } from "@adora/shared/utils/case-conversions";
import { Platform } from "@adora/shared/types/ad-platforms";
import post from "@adora/shared/utils/post";
import patch from "@adora/shared/utils/patch";
import { useEffect } from "react";
import { useReportingStore } from "../store/reporting-ids-store";

const REPORTING_TRACKED_IDS_URL = `${RelativeUrl}/reporting/tracked_ids`;
export const useReportingTrackedIds = (brandId: string | undefined, platform: Platform) => {
  const queryParams = new URLSearchParams();
  queryParams.append("brand_id", brandId || "");
  queryParams.append("platform", platform);

  const { data, error, isLoading } = useSWR<ReportingIdResponse[]>(
    brandId ? `${REPORTING_TRACKED_IDS_URL}?${queryParams.toString()}` : null,
    fetcher
  );

  const { initializePlatform } = useReportingStore();
  const currentPlatform = platform;

  // Initialize store when API data loads
  useEffect(() => {
    if (!isLoading && data && data.length > 0) {
      const campaignIds = data.flatMap((item) => item.campaign_ids);
      const adgroupIds = data.flatMap((item) => item.adgroup_ids);
      initializePlatform(currentPlatform, {
        campaignIds,
        adgroupIds
      });
    }
  }, [data, isLoading, currentPlatform, initializePlatform]);

  return { data: data ? data.map(snakeToCamel) : [], error, isLoading };
};
function getCommonMutateOptions(key: Arguments) {
  return [
    typeof key === "string" && key.startsWith(REPORTING_TRACKED_IDS_URL),
    undefined,
    { revalidate: true }
  ] as const;
}

export const useMutateCreateReportingTrackedIds = () => {
  return useSWRMutation(REPORTING_TRACKED_IDS_URL, createReportingTrackedIds, {
    onSuccess: () => mutate(getCommonMutateOptions)
  });
};
export const useMutateUpdateReportingTrackedIds = (id: string) => {
  return useSWRMutation(`${REPORTING_TRACKED_IDS_URL}/${id}`, updateReportingTrackedIds, {
    onSuccess: () => mutate(getCommonMutateOptions)
  });
};

const createReportingTrackedIds = async (
  key: string,
  { arg }: { arg: { brandId: string | undefined; platform: Platform; campaignIds: string[]; adgroupIds: string[] } }
) => {
  return await post<ReportingIds[]>(
    key,
    {
      brand_id: arg.brandId
    },
    { platform: arg.platform, campaign_ids: arg.campaignIds, adgroup_ids: arg.adgroupIds }
  );
};

const updateReportingTrackedIds = async (
  key: string,
  { arg }: { arg: { brandId: string | undefined; campaignIds: string[]; adgroupIds: string[] } }
) => {
  return await patch<ReportingIds[]>(
    key,
    { brand_id: arg.brandId },
    { campaign_ids: arg.campaignIds, adgroup_ids: arg.adgroupIds }
  );
};
